import type { Translation } from './es.js';

export const en: Translation = {
  common: {
    save: 'Save',
    saving: 'Saving...',
    saved: 'Changes saved',
    saveError: 'Could not save changes',
    unsavedChanges: 'Unsaved changes',
    unsavedPrompt: 'Save your changes or discard them before leaving this page.',
    keepEditing: 'Keep editing',
    discardChanges: 'Discard changes',
    loading: 'Loading...',
    language: 'Language',
    enabled: 'Enabled',
    disabled: 'Disabled',
    close: 'Close',
    discard: 'Discard',
    delete: 'Delete',
  },
  nav: {
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    designer: 'Form Designer',
    shippingRates: 'Shipping rates',
    salesBooster: 'Sales Booster',
    quantityOffers: 'Quantity Offers',
    tracking: 'Tracking Pixels',
    settings: 'Settings',
    appName: 'Releasit COD',
    collapse: 'Collapse',
    expand: 'Expand',
  },
  shippingRates: {
    title: 'Shipping rates',
    addRate: 'Add rate',
    empty: 'You have not created any shipping rates yet. Click "Add rate" to get started.',
    colName: 'Name',
    colPrice: 'Price',
    colCondition: 'Condition',
    colActions: 'Actions',
    alwaysVisible: 'No condition, always visible.',
    edit: 'Edit',
    rateName: 'Rate name',
    rateDescription: 'Rate description',
    ratePrice: 'Rate price',
    conditionsHeading: 'Rate conditions',
    addCondition: 'Add condition',
    conditionType: 'Condition type',
    conditionValue: 'Value',
    product: 'Product',
    selectProduct: 'Select product',
    productCount: '{{count}} products',
    ct_order_total_gte: 'Order total greater or equal than',
    ct_order_total_lt: 'Order total less than',
    ct_quantity_gte: 'Quantity of products greater or equal than',
    ct_quantity_lt: 'Quantity of products less than',
    ct_cart_contains_product: 'Cart contains product',
    ct_cart_not_contains_product: 'Cart does not contain product',
    // Country gating is disabled (see ShippingRates.tsx). These four strings and
    // `countrySummary` below are kept, unused, so switching it back on needs no re-translation.
    countryOnly: 'Enable this rate only for specific countries',
    countryCodes: 'Countries',
    addCountry: 'Add a country...',
    countryCodesHelp: 'The rate shows only for buyers in the countries you pick here.',
    lastRateHelp: 'Your store needs at least one shipping rate, so the last one cannot be deleted.',
    conditionsSummary: '{{count}} condition(s)',
    countrySummary: '{{count}} country(ies)',
  },
  quantityOffers: {
    title: 'Quantity Offers',
    addOffer: 'Add offer',
    search: 'Search',
    searchPlaceholder: 'Search by name...',
    priority: 'Priority',
    enabled: 'Enabled',
    name: 'Name',
    actions: 'Actions',
    newOfferName: 'New offer',
    emptyTitle: 'Configure your quantity offers',
    emptyText:
      'Here you can create quantity offers for your COD form. The offers appear directly on the form or on the product page.',
    noResults: 'No offers found with that name',
    noResultsText: 'Try a different name, or clear the search to see every offer.',
    removedTitle: 'Your offers are not deleted yet',
    removedText: 'Save to delete them for good, or discard to bring them all back.',
    edit: 'Edit',
    drag: 'Drag to reorder',
    duplicate: 'Duplicate',
    delete: 'Delete',
    copyLabel: 'Copy',
    conflictTitle: 'Review your quantity offers',
    conflictWarning:
      'Product {{product}} is used in multiple quantity offers. Please review it, as only one will show and the others won’t work.',
  },
  quantityOfferEditor: {
    addTitle: 'Add offer',
    editTitle: 'Edit offer',
    active: 'Active',
    inactive: 'Inactive',
    back: 'Back',
    saveRequirement: 'Add an offer name and at least one product to save.',
    configureOffer: '1. Configure the offer',
    offerName: 'Offer name',
    offerNamePlaceholder: 'New offer',
    productsHeading: 'Create quantity offers for these products:',
    selectProducts: 'Select products',
    productId: 'Product ID',
    removeProduct: 'Remove product',
    configureTiers: '2. Configure your quantity offers',
    tableTitle: 'Title',
    tableQuantity: 'Quantity',
    addTier: 'Add offer',
    editTier: 'Edit offer',
    deleteTier: 'Delete',
    selectProductPlaceholder: 'Select product',
    defaultTierOne: 'Buy 1 item',
    defaultTierTwo: 'Buy 2 items and get 25% off!',
    defaultSaveZero: 'Save 0%',
    tier: {
      title: 'Title',
      titleHelp: 'Use {product_name} to dynamically insert the product name on this text field.',
      quantity: 'Quantity',
      preselect: 'Preselect this offer',
      bestDeal: 'Show "Best deal" badge',
      bestDealHelp:
        'The badge is shown next to the savings plaque on the preselected offer, so only one offer can carry it.',
      bestDealText: 'Badge text',
      bestDealBgColor: 'Badge background color',
      bestDealTextColor: 'Badge text color',
      bestDealRadius: 'Badge border radius',
      bestDealAnimation: 'Badge animation',
      hideComparePrice: 'Hide the comparison price on the offer',
      discountType: 'Discount type',
      discountPercentage: 'Percentage',
      discountFixed: 'Fixed amount',
      discountNone: 'No discount',
      discountValue: 'Discount value',
      priceColor: 'Price color',
      plaqueText: 'Plaque text',
      plaqueTextColor: 'Plaque text color',
      plaqueBgColor: 'Plaque background color',
      imageUrl: 'Image URL',
    },
    design: {
      heading: '3. Customize the design and behaviour',
      selectMode: 'Select mode and template',
      displayAboveBuyButton: 'Display above buy button (only in pop up form mode)',
      templateClassic: 'Classic',
      templateModern: 'Modern',
      templateVertical: 'Vertical',
      verticalLimitHelp:
        'The vertical template shows up to {{max}} offers so the cards stay readable.',
      verticalUnavailableHelp:
        'The vertical template shows only {{max}} offers so the cards stay readable. You have {{offers}}, so it cannot be selected — buyers would never get to choose the extra ones. Remove an offer to use it.',
      palette: 'Select a palette',
      bgColor: 'Background color',
      borderColor: 'Border color',
      pricesSize: 'Prices size',
      borderRadius: 'Border radius',
      hideProductImage: 'Hide product image',
      hideVariantNumbers: 'Hide numbers next to variant selectors (if a product has variants)',
      disableVariantSelectors: 'Disable the variant selectors (if a product has variants)',
    },
  },
  productPicker: {
    title: 'Add products',
    searchPlaceholder: 'Search products',
    add: 'Add',
    cancel: 'Cancel',
    selectedCount: '{{count}} products selected',
    loadMore: 'Load more',
    loading: 'Loading products...',
    empty: 'No products found.',
    error: 'Could not load products.',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Overview of your cash-on-delivery form',
    appStatus: 'App status',
    appStatusHelp: 'Turn the form on or off across your whole store',
    formViews: 'Form views',
    formOpens: 'Form opens',
    ordersCreated: 'Orders created',
    welcomeTitle: 'Releasit is now on Tiendanube',
    welcomeBody:
      'Your shoppers fill in a cash-on-delivery form on the product page, and a real order is created in your store.',
    welcomeCta: 'Customize your form',
    setupTitle: 'Finish your setup',
    setupDoneTitle: 'Setup complete',
    setupProgress: '{{done}} of {{total}} completed',
    setupHide: 'Hide',
    setupShow: 'Show',
    stepOptional: 'Optional',
    steps: {
      activate: {
        title: 'Activate the app',
        help: 'While it is off, shoppers do not see the form.',
        action: 'Turn on',
      },
      customize: {
        title: 'Customize your form',
        help: 'Match the fields, copy and colors to your brand.',
        action: 'Customize',
      },
      shipping: {
        title: 'Set up shipping rates',
        help: 'Add at least one rate beyond the free shipping you start with.',
        action: 'Set up',
      },
      offer: {
        title: 'Create a quantity offer',
        help: 'Give shoppers a discount for buying more than one unit.',
        action: 'Create',
      },
      pixel: {
        title: 'Add a tracking pixel',
        help: 'Send your cash-on-delivery conversions to your ad platforms.',
        action: 'Add',
      },
      order: {
        title: 'Receive your first order',
        help: 'This ticks itself once a shopper completes the form.',
        action: 'View analytics',
      },
    },
    liveTitle: "What's live right now",
    liveVisibility: 'Visibility',
    liveMode: 'Display mode',
    liveOffers: 'Active quantity offers',
    livePixels: 'Active pixels',
    liveCoverage: 'Coverage',
    liveCoverageOff: 'Off',
    liveChange: 'Change',
    modePopup: 'Popup',
    modeEmbedded: 'Embedded',
    lifetimeRevenue: 'Lifetime revenue',
    noOrdersYet: 'No orders yet',
    performanceTitle: 'Lifetime performance',
    avgOrder: '{{amount}} average order',
    recentOrders: 'Recent orders',
    viewAllOrders: 'View all orders',
    scopeFix: 'Re-authorize the app',
    scope: {
      write_orders: {
        title: 'Orders cannot be created',
        body: 'Your store has not granted the app permission to create orders, so every form submission fails. Enable the permission, then re-authorize the app.',
      },
      read_coupons: {
        title: 'Discount codes cannot be validated',
        body: 'Your store has not granted the app permission to read coupons, so discount codes entered in the form are always rejected. Enable the permission, then re-authorize the app.',
      },
    },
    offerConflictTitle: 'Overlapping quantity offers',
    offerConflictBody:
      '"{{product}}" is in more than one active offer. Only the highest-priority one is shown.',
    offerConflictFix: 'Review offers',
  },
  analytics: {
    title: 'Analytics',
    subtitle: "Your COD form's performance (reported in UTC)",
    dateRange: 'Date range',
    presets: {
      last7: 'Last 7 days',
      last30: 'Last 30 days',
      last90: 'Last 90 days',
      thisMonth: 'This month',
      custom: 'Custom',
    },
    from: 'From',
    to: 'To',
    invalidRange: 'Pick a start date on or before the end date.',
    lifetimeTitle: 'Congratulations!',
    lifetimeBody: 'Your COD form has generated {{amount}} in revenue all-time.',
    formOpens: 'Form opens',
    orders: 'Orders',
    conversionRate: 'Conversion rate',
    revenue: 'Revenue',
    aov: 'Average order value',
    trendOpens: 'Form opens over time',
    trendOrders: 'Orders over time',
    trendRevenue: 'Revenue over time',
    noData: 'No data yet',
    tooltipOpens_one: '{{value}} form open',
    tooltipOpens_other: '{{value}} form opens',
    tooltipOrders_one: '{{value}} order',
    tooltipOrders_other: '{{value}} orders',
    quantityOffers: 'Quantity Offers',
    qoSelections: 'Orders with an offer',
    qoRevenue: 'Revenue contribution',
    qoDiscount: 'Discount given',
    qoNote: 'Display and selection rate will appear once offer impressions are tracked.',
  },
  designer: {
    title: 'Form Designer',
    subtitle: 'Customize and preview your form',
    mode: 'Display mode',
    modePopup: 'Popup',
    modeEmbedded: 'Embedded',
    popupNote:
      "When your customers click on the app's <b>Buy Button</b> the form will open as a popup on the page.",
    embeddedNote:
      "Your customers will see the form embedded directly on their pages so they won't have to click any buttons to open it.",
    tabs: {
      button: 'Button',
      fields: 'Fields',
      coverage: 'Coverage',
      appearance: 'Appearance',
      text: 'Text',
    },
    coverage: {
      heading: 'Shipping coverage',
      description:
        'Select the regions you deliver to. The province, city and postal code fields become dropdowns and are validated when the order is placed.',
      label: 'Coverage',
      countryLabel: 'Country you sell in',
      countryHelp: 'Determines which coverages are available below.',
      countryNone: 'Select a country',
      none: 'No coverage',
      noneForCountry: 'There are no coverages available for {{country}} yet.',
      noneUnknownCountry:
        "We couldn't determine your store's country, so no coverage can be offered.",
      appliesTo: 'Applies to',
      fieldProvince: 'Province',
      fieldCity: 'City',
      fieldPostal: 'Postal code',
    },
    button: {
      text: 'Button text',
      bgColor: 'Background color',
      textColor: 'Text color',
      borderRadius: 'Border radius',
      borderWidth: 'Border width',
      borderColor: 'Border color',
      icon: 'Icon',
      iconBag: 'Bag',
      iconCart: 'Cart',
      iconNone: 'No icon',
    },
    fields: {
      heading: 'Form fields',
      reorderHint: 'Drag to reorder fields. Open a field to edit its options.',
      headerCard: 'Header',
      fieldTitle: '{{label}} field',
      drag: 'Drag to reorder',
      hide: 'Hide field',
      show: 'Show field',
      enabled: 'Visible',
      required: 'Required',
      showIcon: 'Show field icon',
      label: 'Label',
      placeholder: 'Placeholder',
      coverageLocked:
        'This field is part of the coverage you selected, so it always shows. Without it the next dropdown stays empty and coverage stops validating orders.',
      minLength: 'Min length',
      maxLength: 'Max length',
      errorText: 'Invalid value error text',
      errorHelp:
        'If you leave this field empty the app will use your <b>generic field error text</b> if the customer enters an invalid value. You can edit it in the Text tab.',
    },
    fieldsStyle: {
      heading: 'Fields styling',
      hideLabels: 'Hide field labels',
      bgColor: 'Field background color',
      textColor: 'Field text color',
      placeholderColor: 'Placeholder color',
      borderColor: 'Border color',
      borderWidth: 'Border width',
      borderMode: 'Border',
      noBorder: 'No border',
      border: 'Border',
      borderPosition: 'Border position',
      posAll: 'All sides',
      posTop: 'Top',
      posRight: 'Right',
      posBottom: 'Bottom',
      posLeft: 'Left',
      borderRadius: 'Border radius',
      shadow: 'Shadow',
      sizing: 'Sizing',
    },
    appearance: {
      heading: 'General appearance',
      borderRadius: 'Border radius',
      bgColor: 'Background color',
      textColor: 'Text color',
      accentColor: 'Accent color',
    },
    productCard: {
      heading: 'Order summary',
      show: 'Show order summary',
      bgColor: 'Background color',
      borderColor: 'Border color',
      borderWidth: 'Border width',
      borderRadius: 'Border radius',
      hideQuantity: 'Hide quantity controls',
      qcBg: 'Quantity controls background',
      qcBorderColor: 'Quantity controls border color',
      qcBorderWidth: 'Quantity controls border width',
      qcBorderRadius: 'Quantity controls border radius',
    },
    totals: {
      heading: 'Totals summary',
      show: 'Show totals summary',
      subtotalTitle: 'Subtotal title',
      shippingTitle: 'Shipping title',
      totalTitle: 'Total title',
      showTaxes: 'Show taxes message',
      taxesMessage: 'Taxes message',
      bgColor: 'Background color',
      textColor: 'Text color',
      borderColor: 'Border color',
      borderWidth: 'Border width',
      borderRadius: 'Border radius',
      dividerColor: 'Divider color',
      dividerWidth: 'Divider width',
    },
    discount: {
      lineText: 'Discounts line text',
      fieldLabel: 'Discount code field label',
      applyText: 'Apply button text',
      buttonBg: 'Apply button background color',
      buttonText: 'Text color',
      borderColor: 'Border color',
      borderRadius: 'Border radius',
      borderWidth: 'Border width',
      inputBg: 'Input background color',
      inputText: 'Input text color',
      inputBorderColor: 'Input border color',
      inputPlaceholderColor: 'Input placeholder color',
      inputBorderRadius: 'Input border radius',
      inputBorderWidth: 'Input border width',
      invalidError: 'Invalid discount code error text',
    },
    shipping: {
      freeText: 'Free text (used if a shipping rate is free)',
      bgColor: 'Background color',
      textColor: 'Text color',
      borderColor: 'Border color',
      activeBorderColor: 'Active border color',
      borderWidth: 'Border width',
      borderRadius: 'Border radius',
    },
    validation: {
      empty: 'This field cannot be empty.',
      minChars: 'Must be at least {{min}} characters.',
      maxChars: 'Cannot be longer than {{max}} characters.',
      minValue: 'The minimum value is {{min}}.',
      maxValue: 'The maximum value is {{max}}.',
      minItems: 'There must be at least {{min}}.',
      invalid: 'This value is not valid.',
      saveBlocked: 'Check the fields highlighted in red.',
      saveBlockedElsewhere:
        'An invalid setting is blocking the save, but it cannot be edited from this view.',
    },
    text: {
      headerTitle: 'Form title',
      headerIcon: 'Show icon',
      summarySection: 'Order summary',
      shippingLabel: 'Shipping text',
      showDiscount: 'Show discount code',
      shippingMethodSection: 'Shipping method',
      shippingMethodTitle: 'Section title',
      shippingMethodOption: 'Option name',
      shippingMethodPrice: 'Option price',
      addressSection: 'Shipping address',
      terms: 'Terms and conditions text',
      termsUrl: 'Terms and conditions link',
      messagesSection: 'Error messages',
      requiredError: 'Required field text',
      genericError: 'Generic error text',
      genericErrorHelp: 'Used when a field has no error text of its own.',
    },
    fieldNames: {
      first_name: 'First Name',
      last_name: 'Last Name',
      phone: 'Phone',
      address: 'Address',
      address_2: 'Address 2',
      province: 'Province',
      city: 'City',
      postal_code: 'Zip Code',
      email: 'Email',
      order_note: 'Order Note',
      newsletter: 'Newsletter',
      terms: 'Accept Terms',
    },
    blocks: {
      order_summary: 'Order summary',
      totals_summary: 'Totals summary',
      shipping_method: 'Shipping method',
      discount_code: 'Discount code',
      quantity_offers: 'Quantity offers',
      submit: 'Submit button',
      terms: 'Terms and conditions',
      newsletter: 'Newsletter opt-in',
      discountHint: 'Lets your customers enter a discount code. Codes are validated at checkout.',
      quantityOffersHint: 'Manage offers and their design from the Quantity Offers page.',
      shippingRatesHint: 'Create and manage shipping rates on the Shipping Rates page.',
    },
    submit: {
      text: 'Button text',
      textHelp: 'Use {order_total} to show the order total',
      bgColor: 'Background',
      textColor: 'Text color',
      borderRadius: 'Border radius',
      fontSize: 'Font size',
    },
    terms: {
      textColor: 'Text color',
      linkColor: 'Link color',
      fontSize: 'Font size',
      errorHelp:
        'Shown when the buyer submits without accepting. Leave empty to use the general required-field message.',
    },
    newsletter: {
      text: 'Opt-in text',
      textColor: 'Text color',
      fontSize: 'Font size',
    },
    addField: {
      button: 'Add custom field, button or image',
      modalTitle: 'Add element',
      add: 'Add',
      customText: 'Title or text',
      customTextDesc: 'A custom text or title',
      image: 'Image or GIF',
      imageDesc: 'Upload an image or GIF',
      whatsapp: 'WhatsApp button',
      whatsappDesc: 'Button to buy or chat on WhatsApp',
      textInput: 'Text input',
      textInputDesc: 'A text entry field',
      dropdown: 'Dropdown list',
      dropdownDesc: 'A menu of options',
      singleChoice: 'Single choice',
      singleChoiceDesc: 'Options with a single selection',
      checkbox: 'Checkbox',
      checkboxDesc: 'A box to accept or confirm',
      date: 'Date selector',
      dateDesc: 'A field to pick a date',
      link: 'Link button',
      linkDesc: 'A button that opens a link',
    },
    custom: {
      label: 'Label',
      placeholder: 'Placeholder',
      required: 'Required',
      showIcon: 'Show field icon',
      iconLabel: 'Icon',
      connectTo: 'Connect this field to:',
      connectNothing: 'Nothing',
      connect_first_name: 'First name',
      connect_last_name: 'Last name',
      connect_phone: 'Phone',
      connect_email: 'Email',
      connect_address: 'Address',
      connect_number: 'Street number',
      connect_address_2: 'Address 2',
      connect_locality: 'Neighborhood (Barrio)',
      connect_province: 'Province',
      connect_city: 'City',
      connect_postal_code: 'Postal code',
      connect_order_note: 'Order note',
      minLength: 'Min length',
      maxLength: 'Max length',
      allowOnlyNumbers: 'Allow only numbers in this field',
      prefixText: 'Prefix text',
      prefixHelp: "Text that your customers can't change.",
      errorText: 'Invalid value error text',
      options: 'Values separated by comma',
      optionsHelp: 'Enter your values separated by a comma. Example: Red,Green,Blue',
      content: 'Text',
      shortcodesText:
        'Shortcodes: {order_total} for the order total, {product_name} for the product name',
      align: 'Alignment',
      align_left: 'Left',
      align_center: 'Center',
      align_right: 'Right',
      fontSize: 'Font size',
      fontWeight: 'Font weight',
      weight_normal: 'Normal',
      weight_bold: 'Bold',
      textColor: 'Text color',
      imageUrl: 'Image URL',
      imageSize: 'Image size',
      btnText: 'Button text',
      subtitle: 'Button subtitle',
      phone: 'Your WhatsApp phone number',
      phoneHelp: 'Important: include your country code in the phone number.',
      createOrder: 'Create an order when the button is clicked',
      message: 'Pre-filled WhatsApp message',
      shortcodesWa:
        'Shortcodes: {page_url}, {products_summary_with_quantity}, {order_total}, {full_name}, {phone}, {email}, {full_address}, {order_note}',
      animation: 'Button animation',
      anim_none: 'None',
      anim_pulse: 'Pulse',
      anim_shake: 'Shake',
      anim_bounce: 'Bounce',
      anim_tada: 'Tada',
      bgColor: 'Background color',
      borderColor: 'Border color',
      borderRadius: 'Border radius',
      borderWidth: 'Border width',
      shadow: 'Shadow',
      url: 'Link URL',
    },
    preview: 'Preview',
  },
  // Shared page-state copy (empty / error / loading). One source for all eight pages —
  // see components/states.tsx.
  states: {
    errorTitle: 'We could not load this section',
    errorText:
      'Check your connection and try again. If it keeps happening, reopen the app from your store dashboard.',
    retry: 'Try again',
    noDataTitle: 'No data yet',
    noDataText: 'Metrics will show up here once shoppers start using your form.',
  },
  tracking: {
    title: 'Tracking Pixels',
    subtitle: 'Send your cash-on-delivery order conversions to your ad platforms',
    masterEnable: 'Enable tracking',
    masterEnableHelp: 'Turns firing of all configured pixels on or off.',
    requireConsent: 'Require consent before tracking',
    requireConsentHelp:
      'When on, no events are sent to your ad platforms at all: not views and not purchases. The storefront cannot collect a shopper consent signal, so turning this on switches all tracking off.',
    addPixel: 'Add pixel',
    empty: 'Click "Add pixel" to get started.',
    status: {
      active: 'Active',
      inactive: 'Off',
      incomplete: 'Needs setup',
      failing: 'Failing',
      unsupported: 'Cannot fire',
    },
    pixelLabel: '{{provider}} pixel',
    newPixelName: 'New pixel',
    provider: 'Platform',
    providerMeta: 'Meta (Facebook / Instagram)',
    providerTiktok: 'TikTok',
    providerGoogleAds: 'Google Ads',
    providerGa4: 'Google Analytics 4',
    pixelId: 'Pixel ID',
    pixelIdHelpMeta: 'Your Meta pixel ID (digits only).',
    pixelIdHelpTiktok: 'Your TikTok pixel ID.',
    pixelIdHelpGoogleAds: 'Your Google Ads conversion ID (AW-XXXXXXXXX).',
    pixelIdHelpGa4: 'Your GA4 measurement ID (G-XXXXXXX).',
    label: 'Conversion label',
    labelHelp: 'Google Ads only: the conversion action label.',
    enabled: 'Active',
    noServerPathTitle: 'Google Ads cannot be sent server-side',
    noServerPath:
      'Google Ads conversions require the Google Ads API (OAuth2 plus a developer token), which this app does not use. This pixel does not fire. To measure these orders, add a Google Analytics 4 pixel and link GA4 to Google Ads to import the conversion.',
    accessToken: 'Access token',
    accessTokenSet: 'Token saved. Type a new one to replace it.',
    accessTokenPlaceholder: 'Paste access token',
    accessTokenHelp: 'Stored encrypted and never sent to the browser.',
    clearToken: 'Remove token',
    testFail: 'Could not send the test event',
    failing: 'This pixel’s last server-side send failed:',
    delete: 'Delete pixel',
    dupWarningTitle: 'Avoid double-counting conversions',
    dupWarningBody:
      'If you already use the platform’s native Facebook or Google integration, enable only one conversion source. For cash-on-delivery orders (which skip the native checkout) let this app report the conversion; otherwise disable the native source to avoid duplicates.',
  },
  settings: {
    title: 'Settings',
    subtitle: 'General app settings',
    visibility: 'Visibility',
    visibilityHeading: 'Enable or disable your form',
    visibilityHelp:
      'You can disable your form, or enable it only on product pages, only on the cart page, or on both cart and product pages.',
    visDisabled: 'Disabled',
    visCart: 'Only cart page',
    visProduct: 'Only product pages',
    visBoth: 'Both cart and product pages',
    visibilityEnabledNote: 'COD Form is enabled on your store.',
    visibilityDisabledNote: 'The form is disabled and will not appear on your storefront.',
    cartModeTitle: 'Product page order',
    cartModeHeading: 'What the product-page form orders',
    cartModeHelp:
      'On a product page, order only the product being viewed, or the whole cart plus the current product (added when it is not already in the cart).',
    cartModeProductOnly: 'Product only',
    cartModeProductAndCart: 'Product + Cart',
    thankYouTitle: 'Thank-you page',
    thankYouHeading: 'Post-purchase page',
    thankYouHelp:
      'Choose what shoppers see after they place an order: a custom page, a redirect to a URL, or a WhatsApp chat pre-filled with their order details.',
    thankYouEnable: 'Show a custom thank-you page',
    thankYouMode: 'What happens after an order',
    thankYouModePage: 'Show custom page',
    thankYouModeUrl: 'Show a button to a URL',
    thankYouModeWhatsapp: 'Show a button to WhatsApp',
    thankYouPageLabel: 'Page content (HTML)',
    thankYouPlaceholders:
      'Shortcodes: {order_id}, {order_number}, {order_total}, {products_summary_with_quantity}, {product_title}, {product_quantity}, {first_name}, {last_name}, {full_name}, {phone}, {email}, {address}, {address2}, {province}, {city}, {zip_code}, {order_note}, {shipping_rate_name}.',
    thankYouEditHtml: 'Edit the HTML',
    thankYouPreviewLabel: 'Live preview',
    thankYouPreviewEmpty: 'Nothing to preview yet — write or paste your HTML above.',
    thankYouUrlLabel: 'Redirect URL',
    thankYouUrlHelp:
      'After a successful order the shopper sees a button that opens this URL in a new tab (e.g. https://your-site.com/thanks).',
    thankYouUrlRequired: 'Add the URL the button should open, or choose "Custom page" instead.',
    thankYouUrlInvalid:
      'Enter a full address starting with https://, or a path on your store starting with /.',
    thankYouPhoneLabel: 'WhatsApp number',
    thankYouPhoneHelp: 'Include the country code, e.g. 57 300 123 4567.',
    thankYouPhoneRequired:
      'Add the WhatsApp number the button should open, or choose "Custom page" instead.',
    thankYouPhoneInvalid: 'Enter the number with its country code, e.g. 57 300 123 4567.',
    thankYouMessageLabel: 'Pre-filled message',
    thankYouWaPreview: 'Opens',
    templatesHeading: 'Start from a template',
    templatesHelp:
      'Pick a ready-made design and insert it, or choose "Your own HTML" to write the page yourself. To build on a design, copy its HTML first.',
    templateCustomHelp:
      'Write or paste your own HTML in "Edit the HTML" below. To start from one of the designs, select it above and use "Copy HTML".',
    templateCopy: 'Copy HTML',
    templateCopied: 'HTML copied. Paste it into "Your own HTML".',
    templateCopyFailed:
      'Your browser blocked the copy. Insert the template instead, then switch to "Your own HTML" to edit it.',
    templateLanguage: 'Template language',
    templateLanguageHelp:
      "Starts on your store's language. Change it if you write to your buyers in another one — only the template copy changes, not your dashboard.",
    templateCallToConfirm: 'I call buyers to confirm orders',
    templateCallToConfirmHelp:
      'Changes the contact line in the template. Applied when inserting; if you change it later, insert the template again.',
    templateUse: 'Use this template',
    templateCurrent: 'Current template:',
    templateCustom: 'Your own HTML',
    templateNone: 'None yet',
    templateInUse: 'In use',
    templateReplaceTitle: 'Replace the current content',
    templateReplaceBody:
      'You already wrote content in the thank-you page. Inserting this template replaces all of it and cannot be undone.',
    templateReplaceConfirm: 'Replace',
    templateReplaceCancel: 'Cancel',
    templateApplied: 'Template inserted. Remember to save.',
    templateClean: 'Clean',
    templateBold: 'Bold',
    templateReceipt: 'Receipt',
    templateSoft: 'Friendly',
    templateSteps: 'Steps',
  },
};
